import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { HEADERS } from '../api-headers';
import {
  assignCategoryTree,
  transitionToWorkflowStage,
} from '../serialize/post.serialize';
import {
  POST_POST_ADD_RELATED_POSTS,
  POST_PATCH_PROCESS,
  POST_PATCH_PROCESS_SELECTED,
  POST_POST_CREATE_QUICK_STORY_REPORT,
  POST_POST_CREATE_SYNOPSIS,
  POST_POST_DELETE_SYNOPSIS,
  POST_POST_GENERATE_SYNOPSIS,
  POST_GET,
  POST_POST_USER_CATEGORIES,
  POST_GET_SENTIMENT_MANUAL_SCORE,
  POST_PATCH_USER_CATEGORIES,
  POST_PATCH_WORKFLOW_STAGE,
  POST_PATCH_MANUAL_SENTIMENT,
  POST_POST_TRANSLATE,
  POST_POST_CREATE_TRANSLATION,
  POST_POST_UPDATE_QUICK_STORY_REPORT,
  POST_POST_UPDATE_SYNOPSIS,
  POST_POST_IMPORT_BY_URL,
  POST_POST_STORY_POSTS,
  POST_POST_STORY_PARAMS,
  POST_POST_REMOVE_STORY_MEMBERS,
} from '../endpoints/post.endpoints';

export interface ImportByUrlPayload {
  urls: string;
}

import {
  CompanyCategory,
  Post,
  PostSynopsis,
  PostTranslation,
} from './../../core/models/core.models';
import { NomMassUpdateOperation } from 'src/app/core/constants';

import { map, Observable } from 'rxjs';

export interface StoryPostsResponse {
  posts: Post[];
  count_posts: number;
}

/**
 * The manual-sentiment and generate-synopsis endpoints answer with the updated post
 * wrapped in a **one-element array**, hence `response.post[0]` at every call site.
 */
type HasPostResponse = {
  post: Post[];
};

export interface ImportByUrlResponse {
  success: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PostApiService {
  private readonly http = inject(HttpClient);

  getPost(postId: string, feedId: string): Observable<Post> {
    const params = new HttpParams().set('queryId', feedId);

    return this.http.get<Post>(POST_GET(postId), { params });
  }

  getSentimentManualScore(postId: string): Observable<any[]> {
    const url = POST_GET_SENTIMENT_MANUAL_SCORE(postId);

    return this.http.get<any[]>(url);
  }

  /**
   * The categorize dialog's tree: which of the company's categories are already
   * assigned to the given posts.
   */
  getPostCategories(
    postIds: string[],
    selectedPostsFeedId: string,
  ): Observable<CompanyCategory[]> {
    const formData = new FormData();
    postIds.forEach((postId) => formData.append('postIds[]', postId));
    formData.append('selectedPostsFeedId', selectedPostsFeedId);

    return this.http.post<CompanyCategory[]>(
      POST_POST_USER_CATEGORIES,
      formData,
    );
  }

  assignCategoryTree(
    categories: { [key: number]: number[] },
    postIds: string[],
    updateOperation: NomMassUpdateOperation,
  ): Observable<any> {
    const body = assignCategoryTree.toUrlEncoded(
      categories,
      postIds,
      updateOperation,
    );

    return this.http.patch(POST_PATCH_USER_CATEGORIES, body, {
      headers: HEADERS.formUrlEncoded,
    });
  }

  transitionToWorkflowStage(
    postId: string,
    workflowId: string,
    workflowStageId: number,
    feedId: string,
  ): Observable<Post> {
    const body = transitionToWorkflowStage.toUrlEncoded(
      workflowId,
      workflowStageId,
      feedId,
    );

    return this.http.patch<Post>(POST_PATCH_WORKFLOW_STAGE(postId), body, {
      headers: HEADERS.formUrlEncoded,
    });
  }

  applyPostAlgorithm(
    feedId: string,
    postId: string,
    algorithmId: number,
    algorithmParams: any = {},
  ): Observable<Post[]> {
    return this.http.patch<Post[]>(POST_PATCH_PROCESS, {
      query_id: feedId,
      post_id: postId,
      algorithm_id: algorithmId,
      params: algorithmParams,
    });
  }

  applySelectedPostsAlgorithm(
    feedId: string,
    algorithmId: number,
    algorithmParams: any = {},
  ): Observable<Post[][]> {
    return this.http.patch<Post[][]>(POST_PATCH_PROCESS_SELECTED, {
      query_id: feedId,
      algorithm_id: algorithmId,
      params: algorithmParams,
    });
  }

  setPostSentiment(
    feedId: string,
    postId: string,
    sentiment: number,
  ): Observable<HasPostResponse> {
    const body = new URLSearchParams();
    body.append('sentiment_value_manual', sentiment.toString());
    body.append('query_id', feedId);

    return this.http.patch<HasPostResponse>(
      POST_PATCH_MANUAL_SENTIMENT(postId),
      body,
      { headers: HEADERS.formUrlEncoded },
    );
  }

  addRelatedPosts(
    postId: string,
    relatedPostIds: string[],
    feedId: string,
  ): Observable<Post> {
    const body = new URLSearchParams();
    relatedPostIds.forEach((id) => body.append('postIds[]', id));
    body.append('queryId', feedId);

    return this.http.post<Post>(
      POST_POST_ADD_RELATED_POSTS(postId),
      body.toString(),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  updatePostTranslation(
    postId: string,
    languageId: number,
    title: string,
    content: string,
  ): Observable<Post> {
    const body = new URLSearchParams();
    body.append('language_id', languageId.toString());
    body.append('translation[title]', title);
    body.append('translation[content]', content);

    return this.http.post<Post>(
      POST_POST_CREATE_TRANSLATION(postId),
      body.toString(),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  translatePost(
    postId: string,
    languageId: number,
    title: string,
    content: string,
  ): Observable<PostTranslation> {
    const body = new URLSearchParams();
    body.append('targetLanguageId', languageId.toString());
    body.append('properties[title]', title);
    body.append('properties[content]', content);

    return this.http.post<PostTranslation>(
      POST_POST_TRANSLATE(postId),
      body.toString(),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  createQuickStoryReport(
    postId: string,
    languageId: number,
    feedId: string,
  ): Observable<Post> {
    const body = new URLSearchParams();
    body.set('languageId', languageId.toString());
    body.set('queryId', feedId);

    return this.http.post<Post>(
      POST_POST_CREATE_QUICK_STORY_REPORT(postId),
      body.toString(),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  updateQuickStoryReport(
    postId: string,
    languageId: number,
    feedId: string,
    html: string,
  ): Observable<Post> {
    const body = new URLSearchParams();
    body.set('languageId', languageId.toString());
    body.set('queryId', feedId);
    body.set('quickStoryReport[html]', html);
    body.set('quickStoryReport[languageId]', languageId.toString());

    return this.http.post<Post>(
      POST_POST_UPDATE_QUICK_STORY_REPORT(postId),
      body.toString(),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  generateAISynopsis(
    postId: string,
    languageId: number,
  ): Observable<HasPostResponse> {
    const body = new URLSearchParams();
    body.append('language_id', languageId.toString());

    return this.http.post<HasPostResponse>(
      POST_POST_GENERATE_SYNOPSIS(postId),
      body.toString(),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  createSynopsis(
    postId: string,
    languageId: number,
    title: string,
    synopsis: string,
    feedId: string,
  ): Observable<Post> {
    const body = new URLSearchParams();
    body.append('language_id', languageId.toString());
    body.append('title', title);
    body.append('synopsis', synopsis);
    body.append('query_id', feedId);
    body.append('post_id', postId);

    return this.http.post<Post>(
      POST_POST_CREATE_SYNOPSIS(postId),
      body.toString(),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  updateSynopsis(
    postId: string,
    id: number,
    title: string,
    synopsis: string,
  ): Observable<PostSynopsis> {
    const body = new URLSearchParams();
    body.append('title', title);
    body.append('synopsis', synopsis);
    body.append('id', id.toString());
    body.append('post_id', postId);

    return this.http.post<PostSynopsis>(
      POST_POST_UPDATE_SYNOPSIS(postId),
      body.toString(),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  deleteSynopsis(postId: string, id: number): Observable<boolean> {
    const body = new URLSearchParams();
    body.append('id', id.toString());
    body.append('post_id', postId);

    return this.http.post<boolean>(
      POST_POST_DELETE_SYNOPSIS(postId),
      body.toString(),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  /**
   * Retrieves story posts associated with a given post.
   */
  getStoryPosts(
    postId: string,
    params?: {
      queryId?: string;
      orderField?: string;
      sortDirection?: string;
      page?: number;
      itemsPerPage?: number;
      searchQuery?: string;
    },
  ): Observable<StoryPostsResponse> {
    const formData = new FormData();
    if (params?.queryId) {
      formData.append('queryId', params.queryId);
    }
    if (params?.orderField) {
      formData.append('order_field', params.orderField);
    }
    if (params?.sortDirection) {
      formData.append('sort_direction', params.sortDirection);
    }
    if (params?.page != null) {
      formData.append('page', params.page.toString());
    }
    if (params?.itemsPerPage != null) {
      formData.append('items_per_page', params.itemsPerPage.toString());
    }
    if (params?.searchQuery) {
      formData.append('search_query', params.searchQuery);
    }
    return this.http.post<StoryPostsResponse>(
      POST_POST_STORY_POSTS(postId),
      formData,
    );
  }

  /**
   * Retrieves the auto-story run params for a given post. Emits the
   * story_params object, or null if not found.
   */
  getStoryParams(postId: string): Observable<any> {
    return this.http
      .post<{
        success: boolean;
        story_params?: any;
        message?: string;
      }>(POST_POST_STORY_PARAMS(postId), null)
      .pipe(map((res) => (res?.success ? (res.story_params ?? null) : null)));
  }

  /**
   * Removes posts from a story.
   */
  removeStoryMembers(
    storyPostId: string,
    postIds: string[],
    queryId?: string,
  ): Observable<StoryPostsResponse> {
    const formData = new FormData();
    postIds.forEach((id) => formData.append('postIds[]', id));
    if (queryId) {
      formData.append('queryId', queryId);
    }
    return this.http.post<StoryPostsResponse>(
      POST_POST_REMOVE_STORY_MEMBERS(storyPostId),
      formData,
    );
  }

  importByUrl(payload: ImportByUrlPayload): Observable<ImportByUrlResponse> {
    return this.http.post<ImportByUrlResponse>(
      POST_POST_IMPORT_BY_URL,
      payload,
    );
  }
}
